import { useEffect, useState } from "react";
import { Flame, Moon, Crown, Cake, Users, Send, Sparkles, Check, MessageSquare, Mail, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, btnPrimary, inputCls } from "./shared";
import { ScheduledCampaigns } from "./ScheduledCampaigns";

const toLocal = (ms) => {
  const d = new Date(ms);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const SEG_META = {
  aktive: { icon: Flame, desc: "Har brukt en kupong minst 1 gang de siste 45 dagene" },
  inaktive: { icon: Moon, desc: "Har ikke brukt en kupong de siste 60 dagene" },
  vip: { icon: Crown, desc: "Har brukt kupong minst 3 ganger de siste 60 dagene" },
  bursdag: { icon: Cake, desc: "Har bursdag innen 30 dager" },
  alle: { icon: Users, desc: "Alle registrerte kunder" },
};

export default function Kampanjer() {
  const [data, setData] = useState({ grupper: [], aldersgrupper: [], kuponger: [] });
  const [grupper, setGrupper] = useState([]);
  const [aldre, setAldre] = useState([]);
  const toggle = (set) => (v) => set((arr) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]));
  const [preview, setPreview] = useState(null);
  const [preset, setPreset] = useState(null);
  const [tittel, setTittel] = useState("");
  const [navn, setNavn] = useState("");
  const [verdi, setVerdi] = useState("");
  const [utloper, setUtloper] = useState(() => {
    const d = new Date(Date.now() + 30 * 86400000);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  });
  const [kanaler, setKanaler] = useState(["sms"]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [modus, setModus] = useState("na");
  const [start, setStart] = useState(() => toLocal(Date.now() + 86400000));
  const [gjentakelse, setGjentakelse] = useState("en_gang");
  const [slutt, setSlutt] = useState(() => toLocal(Date.now() + 92 * 86400000));
  const [gyldigDager, setGyldigDager] = useState(30);
  const [scheduled, setScheduled] = useState([]);
  const [repeats, setRepeats] = useState({});

  const loadScheduled = () =>
    api.get("/admin/scheduled-campaigns").then((res) => { setScheduled(res.data.items); setRepeats(res.data.gjentakelser); }).catch(() => {});

  const loadSegments = () =>
    api.get("/admin/segments").then((res) => setData(res.data)).catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)));

  useEffect(() => {
    loadSegments();
    loadScheduled();
  }, []);

  useEffect(() => {
    if (!grupper.length) return setPreview(null);
    api
      .get("/admin/segments/preview", { params: { gruppe: grupper.join(","), aldersgruppe: aldre.join(",") } })
      .then((res) => setPreview(res.data))
      .catch(() => setPreview(null));
  }, [grupper, aldre]);

  const choosePreset = (p) => {
    setPreset(p.id);
    setTittel(p.tittel);
    if (!navn) setNavn(p.tittel);
  };

  const utloperOk = utloper && new Date(utloper).getTime() > Date.now();
  const canSend = grupper.length > 0 && tittel.trim().length >= 3 && navn.trim().length >= 3 && preview?.antall > 0 && utloperOk && !sending;

  const send = async () => {
    setSending(true);
    setResult(null);
    try {
      const res = await api.post("/admin/campaigns", {
        navn: navn.trim(),
        tittel: tittel.trim(),
        gruppe: grupper.join(","),
        aldersgruppe: aldre.join(","),
        verdi_kr: Number(verdi) || 0,
        utloper: utloper ? new Date(utloper).toISOString() : null,
        kanaler,
      });
      setResult(res.data);
      if (res.data.sms_konfigurert) {
        toast.success(`Kampanjen er sendt til ${res.data.levert} kunder`);
      } else {
        toast.warning("Kampanjen er opprettet, men SMS er ikke konfigurert ennå. Eksporter lenkene fra Resultater.");
      }
      loadSegments();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const startOk = start && new Date(start).getTime() > Date.now();
  const canSchedule = grupper.length > 0 && tittel.trim().length >= 3 && navn.trim().length >= 3 && preview?.antall > 0 && startOk && !sending;

  const schedule = async () => {
    setSending(true);
    setResult(null);
    try {
      await api.post("/admin/scheduled-campaigns", {
        navn: navn.trim(),
        tittel: tittel.trim(),
        gruppe: grupper.join(","),
        aldersgruppe: aldre.join(","),
        verdi_kr: Number(verdi) || 0,
        kanaler,
        gyldig_dager: Number(gyldigDager) || 30,
        start: new Date(start).toISOString(),
        gjentakelse,
        slutt: gjentakelse === "en_gang" ? null : new Date(slutt).toISOString(),
      });
      toast.success("Kampanjen er planlagt");
      loadScheduled();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const selected = data.grupper.filter((g) => grupper.includes(g.id));
  const ageCount = (a) => selected.reduce((n, g) => n + (g.aldersgrupper.find((x) => x.gruppe === a)?.antall || 0), 0);

  return (
    <div data-testid="kampanjer-page">
      <h1 className="font-serif-display text-3xl font-600 text-[#FDFBF7]">Send kampanje</h1>
      <p className="mt-2 text-sm text-[#A8A29E]">Velg én eller flere kundegrupper (og eventuelt aldersgrupper), velg eller lag en kupong, og send den til alle i utvalget.</p>

      {/* Steg 1: gruppe */}
      <p className="mt-8 text-xs uppercase tracking-[0.2em] text-[#D4AF37]">1. Velg gruppe(r)</p>
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {data.grupper.map((g) => {
          const Icon = SEG_META[g.id].icon;
          const active = grupper.includes(g.id);
          return (
            <button
              key={g.id}
              onClick={() => toggle(setGrupper)(g.id)}
              data-testid={`segment-${g.id}`}
              className={`rounded-2xl border p-4 text-left transition-colors ${
                active ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 bg-[#282421] hover:border-[#D4AF37]/40"
              }`}
            >
              <div className="flex items-center justify-between">
                <Icon className={`h-5 w-5 ${active ? "text-[#D4AF37]" : "text-[#A8A29E]"}`} />
                <span className="text-2xl font-700 text-[#FDFBF7]" data-testid={`segment-count-${g.id}`}>{g.antall}</span>
              </div>
              <p className="mt-3 font-semibold text-[#FDFBF7]">{g.navn}</p>
              <p className="mt-1 text-xs leading-snug text-[#A8A29E]">{SEG_META[g.id].desc}</p>
            </button>
          );
        })}
      </div>

      {/* Steg 2: aldersgruppe */}
      {selected.length > 0 && (
        <>
          <p className="mt-8 text-xs uppercase tracking-[0.2em] text-[#D4AF37]">2. Aldersgrupper (valgfritt – flere kan velges)</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={() => setAldre([])}
              data-testid="age-filter-alle"
              className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                aldre.length === 0 ? "border-[#D4AF37] bg-[#D4AF37] text-[#1A1715]" : "border-white/10 text-[#FDFBF7] hover:border-[#D4AF37]/50"
              }`}
            >
              Alle aldre
            </button>
            {data.aldersgrupper.map((a) => (
              <button
                key={a}
                onClick={() => toggle(setAldre)(a)}
                data-testid={`age-filter-${a}`}
                className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                  aldre.includes(a) ? "border-[#D4AF37] bg-[#D4AF37] text-[#1A1715]" : "border-white/10 text-[#FDFBF7] hover:border-[#D4AF37]/50"
                }`}
              >
                {a} <span className="opacity-70">({ageCount(a)})</span>
              </button>
            ))}
          </div>

          {/* Steg 3: kupong */}
          <p className="mt-8 text-xs uppercase tracking-[0.2em] text-[#D4AF37]">3. Velg kupong</p>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {data.kuponger.map((p) => (
              <button
                key={p.id}
                onClick={() => choosePreset(p)}
                data-testid={`preset-${p.id}`}
                className={`flex items-center justify-between rounded-2xl border px-5 py-4 text-left transition-colors ${
                  preset === p.id ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 bg-[#282421] hover:border-[#D4AF37]/40"
                }`}
              >
                <span className="font-serif-display text-lg text-[#FDFBF7]">{p.tittel}</span>
                {preset === p.id && <Check className="h-5 w-5 text-[#D4AF37]" />}
              </button>
            ))}
            <button
              onClick={() => { setPreset("egen"); setTittel(""); }}
              data-testid="preset-egen"
              className={`flex items-center gap-3 rounded-2xl border px-5 py-4 text-left transition-colors ${
                preset === "egen" ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-dashed border-white/20 bg-[#282421] hover:border-[#D4AF37]/40"
              }`}
            >
              <Sparkles className="h-5 w-5 text-[#D4AF37]" />
              <span className="font-serif-display text-lg text-[#FDFBF7]">Lag din egen kupong</span>
            </button>
          </div>

          <Card className="mt-6" testid="campaign-form">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="block sm:col-span-2">
                <span className="mb-1.5 block text-xs uppercase tracking-wide text-[#A8A29E]">Kupongtekst (det kunden ser)</span>
                <input value={tittel} onChange={(e) => setTittel(e.target.value)} placeholder="F.eks. Gratis kaffe ved kjøp av dessert" data-testid="campaign-title-input" className={inputCls} />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-xs uppercase tracking-wide text-[#A8A29E]">Kampanjenavn (internt)</span>
                <input value={navn} onChange={(e) => setNavn(e.target.value)} placeholder="F.eks. Høstkampanje VIP" data-testid="campaign-name-input" className={inputCls} />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-xs uppercase tracking-wide text-[#A8A29E]">Estimert verdi per innløsning (kr)</span>
                <input type="number" min="0" value={verdi} onChange={(e) => setVerdi(e.target.value)} placeholder="350" data-testid="campaign-value-input" className={inputCls} />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-xs uppercase tracking-wide text-[#A8A29E]">Utløper (dato og klokkeslett)</span>
                <input type="datetime-local" value={utloper} onChange={(e) => setUtloper(e.target.value)} data-testid="campaign-expiry-input" className={`${inputCls} [color-scheme:dark]`} />
                {!utloperOk && <span className="mt-1 block text-xs text-red-400" data-testid="expiry-error">Utløpsdato må være frem i tid</span>}
              </label>
            </div>

            <div className="mt-5">
              <span className="mb-2 block text-xs uppercase tracking-wide text-[#A8A29E]">Send via</span>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setKanaler((k) => (k.includes("sms") ? k.filter((x) => x !== "sms") : [...k, "sms"]))}
                  data-testid="channel-sms"
                  className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors ${
                    kanaler.includes("sms") ? "border-[#D4AF37] bg-[#D4AF37] text-[#1A1715]" : "border-white/10 text-[#FDFBF7] hover:border-[#D4AF37]/50"
                  }`}
                >
                  <MessageSquare className="h-4 w-4" /> SMS
                </button>
                <button
                  type="button"
                  disabled
                  title="E-postutsending av kampanjer er ikke tilgjengelig – e-posttjenesten tillater kun transaksjonelle e-poster"
                  data-testid="channel-epost"
                  className="flex cursor-not-allowed items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-[#A8A29E]/50"
                >
                  <Mail className="h-4 w-4" /> E-post <span className="text-[0.65rem] uppercase">(ikke tilgjengelig)</span>
                </button>
              </div>
              <p className="mt-2 text-xs text-[#A8A29E]">
                E-postkampanjer kan ikke sendes fra appen. Bruk «Eksporter (CSV)» i kundedatabasen for e-postlister. Uten SMS valgt opprettes kun kupong-lenkene.
              </p>
            </div>

            <div className="mt-6 border-t border-white/10 pt-5">
              <span className="mb-2 block text-xs uppercase tracking-wide text-[#A8A29E]">Når skal kampanjen sendes?</span>
              <div className="flex flex-wrap gap-2">
                {[["na", "Send nå"], ["planlegg", "Planlegg / fast kampanje"]].map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setModus(id)}
                    data-testid={`mode-${id}`}
                    className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                      modus === id ? "border-[#D4AF37] bg-[#D4AF37] text-[#1A1715]" : "border-white/10 text-[#FDFBF7] hover:border-[#D4AF37]/50"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {modus === "planlegg" && (
                <div className="mt-4 grid grid-cols-1 gap-4 rounded-xl border border-white/10 bg-[#1A1715]/50 p-4 sm:grid-cols-2" data-testid="schedule-form">
                  <label className="block">
                    <span className="mb-1.5 block text-xs uppercase tracking-wide text-[#A8A29E]">Første utsending</span>
                    <input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} data-testid="schedule-start-input" className={`${inputCls} [color-scheme:dark]`} />
                    {!startOk && <span className="mt-1 block text-xs text-red-400" data-testid="schedule-start-error">Må være frem i tid</span>}
                  </label>
                  <label className="block">
                    <span className="mb-1.5 block text-xs uppercase tracking-wide text-[#A8A29E]">Gjentakelse</span>
                    <select value={gjentakelse} onChange={(e) => setGjentakelse(e.target.value)} data-testid="schedule-repeat-select" className={inputCls}>
                      {Object.entries(repeats).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </label>
                  {gjentakelse !== "en_gang" && (
                    <label className="block">
                      <span className="mb-1.5 block text-xs uppercase tracking-wide text-[#A8A29E]">Gjentas til</span>
                      <input type="datetime-local" value={slutt} onChange={(e) => setSlutt(e.target.value)} data-testid="schedule-end-input" className={`${inputCls} [color-scheme:dark]`} />
                    </label>
                  )}
                  <label className="block">
                    <span className="mb-1.5 block text-xs uppercase tracking-wide text-[#A8A29E]">Kupongen gyldig i (dager etter utsending)</span>
                    <input type="number" min="1" value={gyldigDager} onChange={(e) => setGyldigDager(e.target.value)} data-testid="schedule-days-input" className={inputCls} />
                  </label>
                  <p className="text-xs text-[#A8A29E] sm:col-span-2">
                    Mottakerne beregnes på nytt ved hver utsending ut fra gruppen(e) du har valgt, slik at nye kunder blir med automatisk. Utsendinger sjekkes hvert 15. minutt.
                  </p>
                </div>
              )}
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-5">
              <p className="text-sm text-[#A8A29E]">
                {kanaler.includes("sms") ? "Sendes som SMS til" : "Oppretter kuponger for"}{" "}
                <span className="font-semibold text-[#FDFBF7]" data-testid="preview-count">{preview?.antall ?? "…"}</span> kunder i{" "}
                <span className="text-[#E5C564]">{selected.map((g) => g.navn).join(", ")}{aldre.length ? ` · ${aldre.join(", ")}` : ""}</span>
              </p>
              {modus === "na" ? (
                <button onClick={send} disabled={!canSend} data-testid="send-campaign-btn" className={btnPrimary}>
                  <Send className="h-4 w-4" /> {sending ? "Sender…" : "Send kampanje"}
                </button>
              ) : (
                <button onClick={schedule} disabled={!canSchedule} data-testid="schedule-campaign-btn" className={btnPrimary}>
                  <CalendarClock className="h-4 w-4" /> {sending ? "Lagrer…" : "Planlegg kampanje"}
                </button>
              )}
            </div>

            {result && (
              <div className="mt-4 rounded-xl border border-[#D4AF37]/25 bg-[#D4AF37]/10 px-4 py-3 text-sm text-[#E5C564]" data-testid="campaign-result">
                {result.sms_konfigurert && result.levert + result.feilet > 0
                  ? `Kampanjen er sendt: ${result.levert} levert, ${result.feilet} feilet av ${result.antall_mottakere} mottakere.`
                  : `Kampanjen er opprettet med ${result.antall_mottakere} personlige kuponger uten SMS-utsending – last ned lenkene under «Resultat av kampanjer».`}
              </div>
            )}
          </Card>
        </>
      )}

      <ScheduledCampaigns items={scheduled} onChange={loadScheduled} />
    </div>
  );
}
